import React, { useId, useState } from "react";
import { gql, useApolloClient } from "@apollo/client";
import ArrowDownloadIcon from "@src/lib/icons/18px/arrow-download";
import Table3RowsOutlineIcon from "@src/lib/icons/18px/table-3-rows-outline";
import Spinner from "@src/lib/ui/Spinner";
import { cn } from "@src/lib/utils/cn";
import type { OpusAgentTurnFragment } from "./useOpusAgentChat.generated";
import {
  OPUS_AGENT_CARD_CLASS,
  OPUS_AGENT_CARD_INTERACTIVE_CLASS,
  OpusAgentCardAction,
  OpusAgentCardMedia,
  OpusAgentCardText,
} from "./OpusAgentCard";
import {
  OpusAgentTurnFileDownloadUrlDocument,
  type OpusAgentTurnFileDownloadUrlQuery,
  type OpusAgentTurnFileDownloadUrlQueryVariables,
} from "./OpusAgentFiles.generated";

export type OpusAgentTurnFile = OpusAgentTurnFragment["files"][number];

const TYPE_LABELS: Record<string, string> = { "text/csv": "CSV" };

const formatBytes = (bytes: number): string => {
  if (bytes < 1000) return `${bytes} B`;
  if (bytes < 1_000_000) return `${Math.round(bytes / 1000)} KB`;
  return `${(bytes / 1_000_000).toFixed(1)} MB`;
};

export const fileDetails = (file: OpusAgentTurnFile): string => {
  const parts = [TYPE_LABELS[file.contentType] ?? "File"];
  if (file.rowCount != null)
    parts.push(
      `${file.rowCount.toLocaleString()} ${file.rowCount === 1 ? "row" : "rows"}`,
    );
  parts.push(formatBytes(file.sizeBytes));
  return parts.join(" · ");
};

// Saves the file without navigating: the link answers with
// Content-Disposition: attachment, and loading it in a hidden frame keeps
// beforeunload from firing on the page behind the drawer (builders treat
// that as leaving: presence drops, unsaved-changes prompts open).
const saveFromUrl = (url: string) => {
  const frame = document.createElement("iframe");
  frame.hidden = true;
  frame.src = url;
  document.body.appendChild(frame);
  window.setTimeout(() => frame.remove(), 60_000);
};

type CardState = "idle" | "loading" | "failed" | "expired";

// Announced through a live region beside the button (a button's children are
// presentational, so a region inside it may never be read).
const ANNOUNCEMENTS: Partial<Record<CardState, string>> = {
  failed: "Couldn't download. Try again.",
  expired: "This file has expired.",
};

// The link is signed for a few minutes, so it is fetched on click rather
// than with the turn: a card read an hour ago still downloads.
const OpusAgentFileCard: React.FC<{ file: OpusAgentTurnFile }> = ({ file }) => {
  const client = useApolloClient();
  const labelId = useId();
  // Expiry is read once, at mount; after that only a null link from the
  // server marks the card expired, so an open drawer never flips every card
  // at once on an unrelated re-render.
  const [state, setState] = useState<CardState>(() =>
    new Date(file.expiresAt) <= new Date() ? "expired" : "idle",
  );
  const label =
    state === "expired"
      ? "Expired"
      : state === "failed"
        ? "Couldn't download. Try again."
        : fileDetails(file);
  // aria-disabled rather than disabled, so a keyboard user's focus stays on
  // the card while the link loads and after the file turns out expired.
  const inert = state === "loading" || state === "expired";

  const download = async () => {
    if (inert) return;
    setState("loading");
    try {
      const { data } = await client.query<
        OpusAgentTurnFileDownloadUrlQuery,
        OpusAgentTurnFileDownloadUrlQueryVariables
      >({
        query: OpusAgentTurnFileDownloadUrlDocument,
        variables: { id: file.id },
        fetchPolicy: "network-only",
      });
      const url = data.OpusAgentTurnFileDownloadUrl;
      // Null means the file expired while the card was on screen.
      if (!url) {
        setState("expired");
        return;
      }
      saveFromUrl(url);
      setState("idle");
    } catch {
      setState("failed");
    }
  };

  return (
    <div>
      <button
        type="button"
        onClick={download}
        aria-disabled={inert}
        className={cn(
          OPUS_AGENT_CARD_CLASS,
          "aria-disabled:cursor-default",
          !inert && OPUS_AGENT_CARD_INTERACTIVE_CLASS,
        )}
        data-testid="opus-agent-file"
        aria-label={
          state === "expired"
            ? `${file.name}, expired`
            : `Download ${file.name}`
        }
        aria-describedby={labelId}
      >
        <OpusAgentCardMedia className="bg-bg-secondary">
          <Table3RowsOutlineIcon className="size-4 text-icon-secondary" />
        </OpusAgentCardMedia>
        <OpusAgentCardText
          title={file.name}
          subtitle={label}
          subtitleId={labelId}
          titleTestId="opus-agent-file-name"
          subtitleTestId="opus-agent-file-label"
        />
        {state === "loading" ? (
          <OpusAgentCardAction>
            <Spinner size={12} />
          </OpusAgentCardAction>
        ) : (
          state !== "expired" && (
            <OpusAgentCardAction>
              <ArrowDownloadIcon />
            </OpusAgentCardAction>
          )
        )}
      </button>
      <span
        role="status"
        className="sr-only"
        data-testid="opus-agent-file-status"
      >
        {ANNOUNCEMENTS[state] ?? ""}
      </span>
    </div>
  );
};

export const OpusAgentFiles: React.FC<{ files: OpusAgentTurnFile[] }> = ({
  files,
}) => (
  <div className="mt-2 flex flex-col gap-1.5" data-testid="opus-agent-files">
    {files.map((file) => (
      <OpusAgentFileCard key={file.id} file={file} />
    ))}
  </div>
);

gql`
  query OpusAgentTurnFileDownloadUrl($id: UUID!) {
    OpusAgentTurnFileDownloadUrl(id: $id)
  }
`;
